// Package imports:
import React, { useEffect, useMemo, useState } from "react";
import cx from 'classnames';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import Bugsnag from "@bugsnag/js";
// Component imports:
import Alert from "../../../ui-elements/Alert/Alert";
import Table from "../../../ui-elements/Table/Table";
import DropSelect from "../../../ui-elements/DropSelect/DropSelect";
import LogbirtingaVaktinForm from "../../Watch/LogbirtingaVaktin/LogbirtingaVaktinForm"
import Button from "../../../ui-elements/Button/Button";
import Checkbox from "../../../ui-elements/Input/Checkbox";
// Service imports:
import { GET_KELDAN_API_URL } from '../../../services/config';
import { usePageCounter } from '../../../services/hooks';
import { ErrorMessages } from "../../../services/errorMessages";
import SEO from "../../../services/SEO";
// Type imports:
import { Watch } from "../../../types/MyKeldaTypes";

interface IProps {
    watches?: { [key: string]: Watch[]; } | null,
    setWatches(a: { [key: string]: Watch[]; }): void
}

interface IWatchItem {
    name: string | null,
    term: string,
    id: number,
    active: boolean
}

const LogbirtingaVaktin: React.FC<IProps> = ({
    watches,
    setWatches
}) => {
    const { currentPageIndex, totalPages, goToNextPage, goToPreviousPage, setTotalPages, resetPageCounter } = usePageCounter();
    const [ itemsPerPage, setItemsPerPage ] = useState(10);

    const [ itemCount, setItemCount ] = useState(0);

    const displayData = useMemo(() => {
        if (!watches) return [];
        let data: IWatchItem[] = [];
        const logWatches = watches['LOG'];
        logWatches.forEach((watch) => {
            // If watch exists with same term, edit existing item, otherwise add new item
            let existingWatch = data.find(x => `${x.term.substring(0,6)}-${x.term.substring(6)}` === watch.watchMe);
            if (existingWatch) {
                existingWatch = {
                    ...existingWatch,
                    id: watch.id,
                    active: watch.active
                };
            } else {
                data.push({
                    name: watch.name,
                    term: watch.watchMe,
                    id: watch.id,
                    active: watch.active
                });
            }
        });
        let to = (currentPageIndex + 1) * itemsPerPage;
        let from = to - itemsPerPage;
        
        setItemCount(data.length);
        return data.slice(from, to);
    }, [watches, currentPageIndex, itemsPerPage]);

    const isNextPageDisabled = useMemo(() => {
        if (totalPages === null) return true;
        return ((currentPageIndex + 1) >= totalPages)
    }, [ currentPageIndex, totalPages ]);

    const isPrevPageDisabled = useMemo(() => {
        return (currentPageIndex === 0)
    }, [ currentPageIndex ]);
    
    const handleEdit = async ( id: number | undefined, active: boolean | undefined) => {
        if(id !== undefined && active !== undefined)
        try {
            const requestQuery = new URLSearchParams({
                id: id.toString(),
                active: active.toString(),
            });
            const url = `${GET_KELDAN_API_URL()}/MyKelda/EditWatch`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                }
                const responseBody: { [key: string]: Watch[]; } = await response.json();
                if (responseBody) {
                    setWatches(responseBody)
                } 
            } else {
                throw new Error(ErrorMessages.RequestFailed);
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
        }
    }

    const handleDelete = async (ids: string) => {
        try {
            const requestQuery = new URLSearchParams({
                    ids: ids
            });
            const url = `${GET_KELDAN_API_URL()}/MyKelda/DeleteWatches`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery,
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                } else {
                    const responseBody: { [key: string]: Watch[]; } = await response.json();
                    if (responseBody) {
                        setWatches(responseBody)
                        setItemCount(itemCount - 1)
                    }
                }
            } else {
                throw new Error(ErrorMessages.RequestFailed);
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
        }
    }

    useEffect(() => {
        setTotalPages(itemCount, itemsPerPage);
    }, [itemCount, itemsPerPage]);

    useEffect(() => {
        resetPageCounter();
    }, [itemsPerPage]);

    return (
        <div className="KCL_FyrirtaekjaVaktin">
            <SEO
               title="Lögbirtingavaktin"
               description="Settu fyrirtæki í vöktun og fáðu tilkynningu þegar nýjar niðurstöður birtast í Lögbirtingablaðinu"
               keyword="fyrirtækjavakt, lögbirting, lögbirtingar, lögbirtingablaðið, lögbirtingavakt, dómsbirtingar, dómsmálaauglýsingar"
            />
            <div className="upper-section">
                <div className="left-side">
                    <h3>Lögbirtingavaktin</h3>
                    <p className="description">
                        Settu í vöktun tiltekin orð eins og nafn eða kennitölu og fáðu tilkynningu þegar þau birtast í Lögbirtingablaðinu.
                    </p>
                    <LogbirtingaVaktinForm setData={(data) => setWatches(data)}/>
                </div>
                <Alert
                    type="info"
                    headText="Athugið"
                    text={<p>{  "Keldan býður upp á vöktun á tilteknum upplýsingum sem birtast í ársreikningaskrá, hlutafélagaskrá og Lögbirtingablaði. " + 
                                "Þjónustan er til hagræðis fyrir notendur en Keldan ábyrgist ekki að vöktunin sé algild eða hnökralaus."}</p>}
                />
            </div>
            <div className="middle-section marginTop75px">
                <h3 className="table-title">Lögbirtingar í vöktun</h3>
                <div className="flex-container">
                    <span>Sýna:</span>
                    <DropSelect
                        options={[{
                            label: '10 á síðu',
                            value: '10'
                        }, {
                            label: '50 á síðu',
                            value: '50'
                        }, {
                            label: '100 á síðu',
                            value: '100'
                        }]}
                        defaultValue={itemsPerPage.toString()}
                        size='lg'
                        onChange={(option) => { if (typeof option === 'string') setItemsPerPage(parseInt(option)); resetPageCounter(); }}
                        className='page-size-select'
                    />
                </div>
            </div>
            <Table
                data={displayData}
                columns={[{
                    title: 'í vöktun',
                    dataAccessor: ({term}) => term,
                    textAlign: 'left'
                }, {
                    title: 'staða',
                    dataAccessor: ({active, term, id}) => (
                        <Checkbox 
                            label={(active) ? 'Virk' : 'Óvirk'}
                            name="status"
                            id={`status${term}`}
                            value={active ? 'true' : 'false'}
                            className="statusCheckbox"
                            onClick={() => handleEdit(id, !active)}
                        />
                    ),
                    textAlign: 'left'
                }, {
                    title: '',
                    dataAccessor: ({id}) => 
                    (
                        <Button size="sm" buttonType="secondary" onClick={() => {
                            handleDelete(id.toString());
                        }}>
                            <FontAwesomeIcon icon={faTrashCan} />
                            <span className="deleteBtn__text">Eyða</span>
                        </Button> 
                    ),
                }]}
                tableSize='lg'
            />

            <div className="pagination_wrapper">
                <FontAwesomeIcon  
                    className={cx('pagination_angle', {'disabled': isPrevPageDisabled})} 
                    icon={faAngleLeft}
                    onClick={isPrevPageDisabled ? undefined : goToPreviousPage} />
                
                <span className="pagination_text">síða {currentPageIndex + 1} af {totalPages ?? '...'}</span>
                <FontAwesomeIcon  
                    className={cx('pagination_angle', {'disabled': isNextPageDisabled})} 
                    icon={faAngleRight}
                    onClick={isNextPageDisabled ? undefined : goToNextPage} />
            </div>
            
        </div>
    )
}

export default LogbirtingaVaktin;
